import { z } from "zod";
import { pgTable, pgEnum, uuid, text, boolean, timestamp, jsonb, real, varchar, integer, index, uniqueIndex, unique } from "drizzle-orm/pg-core";
import type { InferSelectModel, InferInsertModel } from "drizzle-orm";
import { relations, sql } from "drizzle-orm";
import type { SourceLevel, ShopStatus, UserRole, ReportReason, SubscriptionStatus } from "./types.js";


export type UserTier = "anonymous" | "free" | "pro" | "power";

export interface User {
  id: string;
  username: string;
  email: string;
  tier: UserTier;
  role?: "user" | "owner" | "admin";
  homeAddress?: string;
  homeCity?: string;
  homePostalCode?: string;
  homeLat?: number;
  homeLng?: number;
  claimedShopIds: string[];
  likedShopIds?: string[];
  savedShopIds?: string[]; // bucketlist
  routeCount: number;
  postsThisWeek: number;
  avatarUrl?: string | null;
  isInsider: boolean;
  extraCredits?: number;
  country?: string | null;
  consent_marketing?: boolean;
  consent_analytical?: boolean;
  consent_ads?: boolean;
  consent_location?: boolean;
}

export interface Shop {
  id: string;
  name: string;
  city: string;
  province: string;
  address: string;
  openingHours: string;
  isVerified: boolean;
  ownerId?: string;
  routeCount: number;
  rating: number; // VintMap Community rating (only if there are VintMap reviews)
  vintMapReviewCount?: number; // Number of VintMap reviews
  ownerRating?: number; // Eigen rating van shop owner
  google_rating?: number | null; // Google rating
  google_review_count?: number | null; // Google review count
  google_reviews_url?: string | null; // Google reviews URL
  imageUrl?: string;
  profileImageUrl?: string | null; // Profile image from shop_photos
  latestPostImageUrl?: string | null; // Latest post image from "deel je buit"
  lat?: number | null;
  lng?: number | null;
  slug?: string;
  has_charity?: boolean;
  has_horeca?: boolean;
  has_card_payment?: boolean;
  has_cash_payment?: boolean;
  categories?: string[];
  description?: string;
  website?: string;
  phone_number?: string;
  likesCount?: number;
  savesCount?: number;
  openingHoursStructured?: any[];
  is_temporarily_closed?: boolean;
  temporarily_closed_until?: string | Date | null;
  temporarily_closed_reason?: string | null;
  country?: string | null;
  price_level_avg?: number | null;
  parking_rating_avg?: number | null;
}

export interface RouteStop {
  shopId: string;
  order: number;
}



export interface Post {
  id: string;
  imageUrl?: string;
  images?: string[];
  caption: string;
  shopId?: string;
  routeId?: string;
  authorId: string;
  authorUsername: string;
  authorAvatarUrl?: string | null;
  asShopId?: string;
  likesCount: number;
  commentsCount: number;
  category: string;
  createdAt: string;
  translations?: Record<string, string> | null;
}

export const insertRouteSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  region: z.string().optional(),
  city: z.string().optional(),
  stops: z.array(z.object({
    shopId: z.string(),
    order: z.number()
  })),
  shop_ids: z.array(z.string()).optional(),
  is_public: z.boolean().default(false).optional(),
  isPublic: z.boolean().optional(), // Backwards compatibility
  start_address: z.any().optional(),
  end_address: z.any().optional(),
  extra_stop: z.any().optional(),
  startFromHome: z.boolean().optional(),
  endAtHome: z.boolean().optional()
});

export type InsertRoute = z.infer<typeof insertRouteSchema>;

export const insertPostSchema = z.object({
  imageUrl: z.string().url().optional(), // Fallback voor oude posts
  images: z.array(z.string().url()).min(1).max(4), // 1-4 foto's verplicht
  caption: z.string().max(500),
  shopId: z.string().optional(),
  routeId: z.string().optional(),
  asShopId: z.string().optional(),
  category: z.string(),
  price_paid: z.number().int().optional().nullable(),
  currency: z.string().optional().default("EUR")
});

export type InsertPost = z.infer<typeof insertPostSchema>;

export const insertUserSchema = z.object({
  username: z.string().min(1).max(50),
  email: z.string().email(),
  tier: z.enum(["anonymous", "free", "pro", "power"]).default("free"),
  homeAddress: z.string().optional(),
  claimedShopIds: z.array(z.string()).default([]),
  avatarUrl: z.string().url().optional().nullable()
});

export type InsertUser = z.infer<typeof insertUserSchema>;

export const updateShopSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  address: z.string().optional(),
  postal_code: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  openingHours: z.union([z.string(), z.array(z.any())]).optional(),
  isVerified: z.boolean().optional(),
  ownerId: z.string().optional(),
  imageUrl: z.string().url().optional(),
  bannerUrl: z.string().url().nullish(),
  has_charity: z.boolean().optional(),
  has_card_payment: z.boolean().optional(),
  has_cash_payment: z.boolean().optional(),
  description: z.string().optional(),
  website: z.string().optional(),
  phone_number: z.string().optional(),
  categories: z.array(z.string()).optional(),
  is_temporarily_closed: z.boolean().optional(),
  temporarily_closed_until: z.string().datetime().nullable().optional(),
  temporarily_closed_reason: z.string().max(200).nullable().optional(),
  facadeImageUrl: z.string().url().optional().nullable(),
});

export type UpdateShop = z.infer<typeof updateShopSchema>;

export const PROVINCES = [
  "Noord-Holland",
  "Zuid-Holland",
  "Utrecht",
  "Noord-Brabant",
  "Gelderland",
  "Overijssel",
  "Limburg",
  "Friesland",
  "Fryslân",
  "Groningen",
  "Drenthe",
  "Flevoland",
  "Zeeland"
] as const;

export const CATEGORIES = [
  "Meubels",
  "Kleding & Schoenen",
  "Boeken & Media (LP's, CD's)",
  "Woondecoratie & Kunst",
  "Keuken- & Huishoudelijk",
  "Elektronica (klein)",
  "Speelgoed & Spellen",
  "Sieraden & Accessoires"
] as const;

export const POST_CATEGORIES = [
  ...CATEGORIES,
  "Winkelnieuws"
] as const;

export const FREE_TIER_LIMITS = {
  maxRoutes: 5,
  maxStopsPerRoute: 10,
  maxPostsPerWeek: 1,
  optimizationsPerDay: 0,
  optimizationsPerMonth: 0,
  interactiveMapViewsPerDay: 5,
  interactiveMapViewsPerMonth: 20,
} as const;

export const PRO_TIER_LIMITS = {
  maxRoutes: 1000, // Effectively unlimited
  maxStopsPerRoute: 10, // Matching free tier limit
  maxPostsPerWeek: 1000,
  optimizationsPerDay: 10,
  optimizationsPerMonth: 60,
  interactiveMapViewsPerDay: 20,
  interactiveMapViewsPerMonth: 240,
} as const;

export const OWNER_PRICING = {
  firstShop: 19.95,
  additionalShop: 19.95
} as const;


export const SUBSCRIPTION_PRICING = {
  monthly: {
    price: 2.99,
    priceId: 'price_1SrQudQUbPAqX0Hpevznvm45',
    label: 'Maandelijks'
  },
  yearly: {
    price: 29.99,
    priceId: 'price_1SwjKOQUbPAqX0HpXxkKyfAm',
    label: 'Jaarlijks'
  }
} as const;

// Legacy constant for backwards compatibility during migration (using yearly price / 12 approx)
export const PRO_PRICE = 2.50; // approx monthly cost if yearly


// ============================================================================
// Drizzle ORM Schema Definitions for Supabase Database
// ============================================================================

// PostgreSQL Enums
export const sourceLevelEnum = pgEnum("source_level", ["OWNER", "COMMUNITY", "GOOGLE", "ADMIN"]);
export const shopStatusEnum = pgEnum("shop_status", ["active", "verification_pending", "closed", "pending_review", "unclaimed", "claimed", "verified"]);
export const userRoleEnum = pgEnum("user_role", ["user", "owner", "admin"]);
export const reportReasonEnum = pgEnum("report_reason", ["CLOSED", "WRONG_INFO", "OTHER", "CLOSED_PERMANENTLY", "spam", "inappropriate", "offensive", "misleading", "copyright", "NOT_A_KRINGLOOP"]);
export const subscriptionStatusEnum = pgEnum("subscription_status", ["active", "canceled", "past_due"]);

// New enums for organizations + shops model
export const orgRoleEnum = pgEnum("org_role", ["owner", "admin", "member"]);
export const shopPlanStatusEnum = pgEnum("shop_plan_status", ["inactive", "active", "past_due", "canceled"]);
export const postVisibilityEnum = pgEnum("post_visibility", ["visible", "hidden"]);
export const offerStatusEnum = pgEnum("offer_status", ["active", "reserved", "expired", "removed"]);
export const reservationStatusEnum = pgEnum("reservation_status", ["active", "expired", "canceled", "completed"]);
export const batchJobStatusEnum = pgEnum("batch_job_status", ["pending", "running", "paused", "completed", "failed", "cancelled"]);
export const batchJobItemStatusEnum = pgEnum("batch_job_item_status", ["pending", "processing", "completed", "failed"]);
export const moderationStatusEnum = pgEnum("moderation_status", ["pending", "green", "amber", "red", "removed"]);
export const consentTypeEnum = pgEnum("consent_type", ["marketing", "analytical", "ads", "location", "terms_of_service"]);
export const deletionStatusEnum = pgEnum("deletion_status", ["pending", "processing", "completed", "cancelled"]);
export const categoryEnum = pgEnum("category", [
  "Meubels",
  "Kleding & Schoenen",
  "Boeken & Media (LP's, CD's)",
  "Woondecoratie & Kunst",
  "Keuken- & Huishoudelijk",
  "Elektronica (klein)",
  "Speelgoed & Spellen",
  "Sieraden & Accessoires"
]);

export const routingProviderEnum = pgEnum("routing_provider", ["google", "ors", "graphhopper", "locationiq", "custom", "geoapify"]);

// Users table - extends Supabase auth.users
export const users = pgTable("users", {
  id: uuid("id").primaryKey(), // References Supabase auth.users(id)
  username: text("username"),
  email: text("email"),
  role: userRoleEnum("role").notNull().default("user"),
  tier: text("tier").notNull().default("free"), // 'free' | 'pro' | 'power'
  suspended_until: timestamp("suspended_until"),
  suspension_reason: text("suspension_reason"),
  home_address: text("home_address"),
  home_city: text("home_city"),
  home_postal_code: text("home_postal_code"),
  home_lat: real("home_lat"),
  home_lng: real("home_lng"),
  avatar_url: text("avatar_url"),
  is_insider: boolean("is_insider").notNull().default(false),
  extra_credits: integer("extra_credits").notNull().default(0),
  country: varchar("country", { length: 100 }).notNull().default("NL"),

  // Profile fields
  name: text("name"),
  location: text("location"),
  hide_location: boolean("hide_location").notNull().default(true),
  social_links: jsonb("social_links").notNull().default({}),
  style_text: text("style_text"),
  looking_for_text: text("looking_for_text"),
  style_photos: text("style_photos").array().notNull().default([]),
  profile_completed: boolean("profile_completed").notNull().default(false),
  is_wishlist_public: boolean("is_wishlist_public").notNull().default(false),
  draft_route: jsonb("draft_route"),

  wishlist_share_token: text("wishlist_share_token").unique(),
  deletion_token: text("deletion_token").unique(),
  deletion_requested_at: timestamp("deletion_requested_at"),

  // AVG / Privacy Consent Flags
  consent_marketing: boolean("consent_marketing").notNull().default(false),
  consent_analytical: boolean("consent_analytical").notNull().default(false),
  consent_ads: boolean("consent_ads").notNull().default(false),
  consent_location: boolean("consent_location").notNull().default(false),
  ad_preferences: jsonb("ad_preferences").notNull().default({}),
  last_consent_at: timestamp("last_consent_at"),

  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
  profile_updated: timestamp("profile_updated"),
}, (table) => ({
  roleIdx: index("users_role_idx").on(table.role),
  tierIdx: index("users_tier_idx").on(table.tier),
  usernameUnique: uniqueIndex("users_username_unique_idx").on(table.username),
}));

export const follows = pgTable("follows", {
  id: uuid("id").primaryKey().defaultRandom(),
  follower_id: uuid("follower_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  followed_user_id: uuid("followed_user_id").references(() => users.id, { onDelete: "cascade" }),
  followed_shop_id: uuid("followed_shop_id").references(() => shops.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  followerIdx: index("idx_follows_follower").on(table.follower_id),
  followedUserIdx: index("idx_follows_followed_user").on(table.followed_user_id),
  followedShopIdx: index("idx_follows_followed_shop").on(table.followed_shop_id),
  createdIdx: index("idx_follows_created").on(table.created_at),
  // Note: unique constraint with multiple nulls needs careful handling in some PG versions
  // but we'll follow the spec for indices.
}));

export type Follow = InferSelectModel<typeof follows>;
export type InsertFollow = InferInsertModel<typeof follows>;

export type DbUser = InferSelectModel<typeof users>;
export type InsertDbUser = InferInsertModel<typeof users>;

// Shops table (updated with new fields)
export const shops = pgTable("shops", {
  id: uuid("id").primaryKey().defaultRandom(),
  organization_id: uuid("organization_id").references(() => organizations.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  address: text("address").notNull(),
  openingHours: text("opening_hours"),
  city: text("city").notNull(),
  postal_code: varchar("postal_code", { length: 20 }).notNull(),
  country: varchar("country", { length: 100 }).notNull().default("NL"),
  province: text("province"), // Province for SEO-friendly URLs
  slug: text("slug").unique(), // Unique slug for SEO-friendly URLs (province/city/name)
  share_image_url: text("share_image_url"), // Generated OG image on Supabase
  lat: real("lat"), // Now nullable
  lng: real("lng"), // Now nullable
  google_place_id: text("google_place_id"),
  is_premium: boolean("is_premium").notNull().default(false),
  claimed_by_user_id: uuid("claimed_by_user_id").references(() => users.id, { onDelete: "set null" }), // Nullable for unclaimed shops
  status: shopStatusEnum("status").notNull().default("pending_review"), // Default to pending_review for unclaimed shops
  created_by_user_id: uuid("created_by_user_id").references(() => users.id, { onDelete: "set null" }),
  verification_started_at: timestamp("verification_started_at"),
  verification_deadline_at: timestamp("verification_deadline_at"),
  last_google_check_at: timestamp("last_google_check_at"),
  // New fields for organizations + shops model
  is_claimed: boolean("is_claimed").notNull().default(false),
  claimed_at: timestamp("claimed_at"),
  plan_status: shopPlanStatusEnum("plan_status").notNull().default("inactive"),
  has_charity: boolean("has_charity").notNull().default(false),
  has_horeca: boolean("has_horeca").notNull().default(false),
  // Google rating snapshot (display-only)
  // Google rating snapshot (display-only)
  google_rating: real("google_rating"),
  google_review_count: integer("google_review_count"),
  google_rating_updated_at: timestamp("google_rating_updated_at"),
  // Verification & test-shop fields
  verification_code: text("verification_code"),
  verification_status: text("verification_status").notNull().default("unverified"),
  verification_requested_at: timestamp("verification_requested_at"),
  verification_paid_at: timestamp("verification_paid_at"),
  verification_letter_printed_at: timestamp("verification_letter_printed_at"),
  verification_letter_print_count: integer("verification_letter_print_count").notNull().default(0),
  verification_letter_sent_at: timestamp("verification_letter_sent_at"),
  is_test_shop: boolean("is_test_shop").notNull().default(false),
  has_card_payment: boolean("has_card_payment").notNull().default(false),
  has_cash_payment: boolean("has_cash_payment").notNull().default(false),
  categories: jsonb("categories"),
  translations: jsonb("translations").$type<Record<string, string>>().notNull().default({}),
  description: text("description"),
  website: text("website"),
  phone_number: text("phone_number"),
  imageUrl: text("image_url"),
  is_verified: boolean("is_verified").notNull().default(false),
  saves_count: integer("saves_count").notNull().default(0), // Bucketlist count
  likes_count: integer("likes_count").notNull().default(0), // Favorites count
  vintmap_rating: real("vintmap_rating").notNull().default(0),
  vintmap_review_count: integer("vintmap_review_count").notNull().default(0),
  price_level_avg: real("price_level_avg"), // Average 1-3 from reviews
  parking_rating_avg: real("parking_rating_avg"), // Average 1-3 from reviews
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
  is_temporarily_closed: boolean("is_temporarily_closed").notNull().default(false),
  temporarily_closed_until: timestamp("temporarily_closed_until"),
  temporarily_closed_reason: text("temporarily_closed_reason"),
  moderation_status: moderationStatusEnum("moderation_status").notNull().default("pending"),
}, (table) => ({
  cityIdx: index("shops_city_idx").on(table.city),
  statusIdx: index("shops_status_idx").on(table.status),
  claimedByIdx: index("shops_claimed_by_idx").on(table.claimed_by_user_id),
  googlePlaceIdIdx: index("shops_google_place_id_idx").on(table.google_place_id),
  locationIdx: index("shops_location_idx").on(table.lat, table.lng),
  organizationIdIdx: index("shops_organization_id_idx").on(table.organization_id),
  planStatusIdx: index("shops_plan_status_idx").on(table.plan_status),
  googleRatingIdx: index("shops_google_rating_idx").on(table.google_rating),
  provinceIdx: index("shops_province_idx").on(table.province),
  slugIdx: index("shops_slug_idx").on(table.slug),
  apiSortIdx: index("shops_api_sort_idx").on(
    sql`COALESCE(NULLIF(${table.vintmap_rating}, 0), NULLIF(${table.google_rating}, 0), 0) DESC`,
    sql`(COALESCE(${table.vintmap_review_count}, 0) + COALESCE(${table.google_review_count}, 0)) DESC`
  ),
}));

export type DbShop = InferSelectModel<typeof shops>;
export type InsertDbShop = InferInsertModel<typeof shops>;

// Shop posts table
export const shop_posts = pgTable("shop_posts", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }),
  created_by_user_id: uuid("created_by_user_id").references(() => users.id, { onDelete: "set null" }),
  title: text("title").notNull(),
  content: text("content"),
  image_url: text("image_url"),
  visibility: text("visibility").notNull().default("public"),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  shopIdIdx: index("shop_posts_shop_id_idx").on(table.shop_id),
}));

export type DbShopPost = InferSelectModel<typeof shop_posts>;
export type InsertDbShopPost = InferInsertModel<typeof shop_posts>;

// Shop fields table - stores flexible fields with authority hierarchy
export const shopFields = pgTable("shop_fields", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }),
  field_name: text("field_name").notNull(), // e.g., "opening_hours", "website_url", "photos"
  source_level: sourceLevelEnum("source_level").notNull(),
  value_json: jsonb("value_json").notNull(), // Flexible JSON value storage
  approved_by_user_id: uuid("approved_by_user_id").references(() => users.id, { onDelete: "set null" }),
  locked: boolean("locked").notNull().default(false), // Prevents Google overwrite
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  shopFieldIdx: index("shop_fields_shop_field_idx").on(table.shop_id, table.field_name),
  sourceLevelIdx: index("shop_fields_source_level_idx").on(table.source_level),
  shopIdIdx: index("shop_fields_shop_id_idx").on(table.shop_id),
}));

export type ShopField = InferSelectModel<typeof shopFields>;
export type InsertShopField = InferInsertModel<typeof shopFields>;

// Shop reports table
export const shopReports = pgTable("shop_reports", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }),
  reporter_user_id: uuid("reporter_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  reason: reportReasonEnum("reason").notNull(),
  description: text("description"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  shopIdIdx: index("shop_reports_shop_id_idx").on(table.shop_id),
  reporterIdIdx: index("shop_reports_reporter_id_idx").on(table.reporter_user_id),
}));

export type ShopReport = InferSelectModel<typeof shopReports>;
export type InsertShopReport = InferInsertModel<typeof shopReports>;

// Shop verifications table
export const shopVerifications = pgTable("shop_verifications", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }).unique(),
  verification_token: text("verification_token").notNull().unique(),
  expires_at: timestamp("expires_at").notNull(),
  used_at: timestamp("used_at"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  shopIdIdx: index("shop_verifications_shop_id_idx").on(table.shop_id),
  tokenIdx: index("shop_verifications_token_idx").on(table.verification_token),
  expiresAtIdx: index("shop_verifications_expires_at_idx").on(table.expires_at),
}));

export type ShopVerification = InferSelectModel<typeof shopVerifications>;
export type InsertShopVerification = InferInsertModel<typeof shopVerifications>;

// Location edits log for moderation
export const locationEdits = pgTable("location_edits", {
  id: uuid("id").primaryKey().defaultRandom(),
  location_id: uuid("location_id").references(() => shops.id, { onDelete: "cascade" }),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "set null" }), // Insider who made the edit
  old_data: jsonb("old_data"),
  new_data: jsonb("new_data"),
  status: text("status").notNull().default("pending"), // 'pending', 'approved', 'rejected'
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  locationIdIdx: index("location_edits_location_id_idx").on(table.location_id),
  userIdIdx: index("location_edits_user_id_idx").on(table.user_id),
  statusIdx: index("location_edits_status_idx").on(table.status),
}));

export type LocationEdit = InferSelectModel<typeof locationEdits>;
export type InsertLocationEdit = InferInsertModel<typeof locationEdits>;

// User subscriptions table - Routes Pro subscriptions
export const userSubscriptions = pgTable("user_subscriptions", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  stripe_customer_id: text("stripe_customer_id").notNull(),
  stripe_subscription_id: text("stripe_subscription_id").notNull(),
  status: subscriptionStatusEnum("status").notNull().default("active"),
  current_period_end: timestamp("current_period_end"),
  tier: text("tier").notNull().default("pro"), // Always 'pro' for now
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  userIdIdx: index("user_subscriptions_user_id_idx").on(table.user_id),
  stripeCustomerIdIdx: index("user_subscriptions_stripe_customer_id_idx").on(table.stripe_customer_id),
  stripeSubscriptionIdIdx: index("user_subscriptions_stripe_subscription_id_idx").on(table.stripe_subscription_id),
}));

export type UserSubscription = InferSelectModel<typeof userSubscriptions>;
export type InsertUserSubscription = InferInsertModel<typeof userSubscriptions>;

// Shop subscriptions table - Shop Premium subscriptions
export const shopSubscriptions = pgTable("shop_subscriptions", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }).unique(),
  owner_user_id: uuid("owner_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  stripe_customer_id: text("stripe_customer_id").notNull(),
  stripe_subscription_id: text("stripe_subscription_id").notNull(),
  status: subscriptionStatusEnum("status").notNull().default("active"),
  price_tier: text("price_tier").notNull(), // 'single_10' | 'multi_7'
  current_period_end: timestamp("current_period_end"),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  shopIdIdx: index("shop_subscriptions_shop_id_idx").on(table.shop_id),
  ownerUserIdIdx: index("shop_subscriptions_owner_user_id_idx").on(table.owner_user_id),
  stripeCustomerIdIdx: index("shop_subscriptions_stripe_customer_id_idx").on(table.stripe_customer_id),
  stripeSubscriptionIdIdx: index("shop_subscriptions_stripe_subscription_id_idx").on(table.stripe_subscription_id),
}));

export type ShopSubscription = InferSelectModel<typeof shopSubscriptions>;
export type InsertShopSubscription = InferInsertModel<typeof shopSubscriptions>;

// Ownerships table - multi-shop claims per user
export const ownerships = pgTable("ownerships", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }),
  is_primary: boolean("is_primary").notNull().default(false),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userIdIdx: index("ownerships_user_id_idx").on(table.user_id),
  shopIdIdx: index("ownerships_shop_id_idx").on(table.shop_id),
  userShopUniqueIdx: index("ownerships_user_shop_unique_idx").on(table.user_id, table.shop_id),
}));

export type Ownership = InferSelectModel<typeof ownerships>;
export type InsertOwnership = InferInsertModel<typeof ownerships>;

// Legacy subscriptions table (deprecated, use shop_subscriptions instead)
export const subscriptions = pgTable("subscriptions", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }),
  stripe_customer_id: text("stripe_customer_id").notNull(),
  stripe_subscription_id: text("stripe_subscription_id").notNull(),
  status: subscriptionStatusEnum("status").notNull().default("active"),
  started_at: timestamp("started_at").notNull().defaultNow(),
  canceled_at: timestamp("canceled_at"),
}, (table) => ({
  shopIdIdx: index("subscriptions_shop_id_idx").on(table.shop_id),
  stripeCustomerIdIdx: index("subscriptions_stripe_customer_id_idx").on(table.stripe_customer_id),
  stripeSubscriptionIdIdx: index("subscriptions_stripe_subscription_id_idx").on(table.stripe_subscription_id),
}));

export type Subscription = InferSelectModel<typeof subscriptions>;
export type InsertSubscription = InferInsertModel<typeof subscriptions>;

// Drizzle Relations
export const usersRelations = relations(users, ({ many }) => ({
  claimedShops: many(shops),
  shopFields: many(shopFields),
  shopReports: many(shopReports),
}));

export const shopsRelations = relations(shops, ({ one, many }) => ({
  claimedBy: one(users, {
    fields: [shops.claimed_by_user_id],
    references: [users.id],
  }),
  fields: many(shopFields),
  reports: many(shopReports),
  subscriptions: many(subscriptions),
  shopSubscription: one(shopSubscriptions, {
    fields: [shops.id],
    references: [shopSubscriptions.shop_id],
  }),
  verification: one(shopVerifications, {
    fields: [shops.id],
    references: [shopVerifications.shop_id],
  }),
  ownerships: many(ownerships),
}));

export const shopFieldsRelations = relations(shopFields, ({ one }) => ({
  shop: one(shops, {
    fields: [shopFields.shop_id],
    references: [shops.id],
  }),
  approvedBy: one(users, {
    fields: [shopFields.approved_by_user_id],
    references: [users.id],
  }),
}));

export const shopReportsRelations = relations(shopReports, ({ one }) => ({
  shop: one(shops, {
    fields: [shopReports.shop_id],
    references: [shops.id],
  }),
  reporter: one(users, {
    fields: [shopReports.reporter_user_id],
    references: [users.id],
  }),
}));

export const subscriptionsRelations = relations(subscriptions, ({ one }) => ({
  shop: one(shops, {
    fields: [subscriptions.shop_id],
    references: [shops.id],
  }),
}));

export const userSubscriptionsRelations = relations(userSubscriptions, ({ one }) => ({
  user: one(users, {
    fields: [userSubscriptions.user_id],
    references: [users.id],
  }),
}));

export const shopSubscriptionsRelations = relations(shopSubscriptions, ({ one }) => ({
  shop: one(shops, {
    fields: [shopSubscriptions.shop_id],
    references: [shops.id],
  }),
  owner: one(users, {
    fields: [shopSubscriptions.owner_user_id],
    references: [users.id],
  }),
}));

export const ownershipsRelations = relations(ownerships, ({ one }) => ({
  user: one(users, {
    fields: [ownerships.user_id],
    references: [users.id],
  }),
  shop: one(shops, {
    fields: [ownerships.shop_id],
    references: [shops.id],
  }),
}));

// Routes table (User created shop routes)
export const routes = pgTable("routes", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  description: text("description"),
  translations: jsonb("translations").$type<Record<string, string>>().notNull().default({}),

  // Array of shop UUIDs in route order
  shop_ids: uuid("shop_ids").array().notNull(),

  is_public: boolean("is_public").notNull().default(false),

  // Custom addresses (ONLY for private routes - NULL for public)
  start_address: jsonb("start_address").$type<{
    source: 'home' | 'custom' | 'current';
    address?: string;
    lat?: number;
    lng?: number;
    city?: string;
    postal_code?: string;
  }>(),
  end_address: jsonb("end_address").$type<{
    source: 'home' | 'custom' | 'current';
    address?: string;
    lat?: number;
    lng?: number;
    city?: string;
    postal_code?: string;
  }>(),
  extra_stop: jsonb("extra_stop").$type<{
    source: 'home' | 'custom' | 'current';
    address?: string;
    lat?: number;
    lng?: number;
    city?: string;
    postal_code?: string;
  }>(),

  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),

  // New columns
  slug: text("slug"),
  share_image_url: text("share_image_url"),
  saves_count: integer("saves_count").notNull().default(0),
  likes_count: integer("likes_count").notNull().default(0),
  region: text("region"),
  city: text("city"),
  country: text("country").notNull().default("NL"),
  vintmap_rating: real("vintmap_rating").notNull().default(0),
  vintmap_review_count: integer("vintmap_review_count").notNull().default(0),
  total_distance_meters: integer("total_distance_meters").default(0),

  // Caching for Google Maps (Policy compliant: 30 days)
  google_route_data: jsonb("google_route_data").$type<{
    distance: string;
    duration: string;
    polyline: string;
  }>(),
  google_route_data_public: jsonb("google_route_data_public").$type<{
    distance: string;
    duration: string;
    polyline: string;
  }>(),
  google_route_data_private: jsonb("google_route_data_private").$type<{
    distance: string;
    duration: string;
    polyline: string;
  }>(),
  google_last_calculated_at: timestamp("google_last_calculated_at"),

  // OSM Route Data
  osm_route_data: jsonb("osm_route_data").$type<{
    distance: string;
    duration: string;
    polyline: string;
    provider: string;
  }>(),
  osm_route_data_public: jsonb("osm_route_data_public").$type<{
    distance: string;
    duration: string;
    polyline: string;
    provider: string;
  }>(),
  osm_last_calculated_at: timestamp("osm_last_calculated_at"),

  // Route thumbnail (1200x1200 with VintMap overlay)
  thumbnail_url: text("thumbnail_url"),
  last_crawled: timestamp("last_crawled"),
}, (table) => ({
  userIdx: index("routes_user_id_idx").on(table.user_id),
  isPublicIdx: index("routes_is_public_idx").on(table.is_public),
  createdAtIdx: index("routes_created_at_idx").on(table.created_at),
}));

export type DbRoute = InferSelectModel<typeof routes>;
export type InsertDbRoute = InferInsertModel<typeof routes>;

export interface Route extends DbRoute {
  stopsData?: Shop[];
  creatorUsername?: string;
  stops?: { shopId: string; order: number }[];
  city: string | null;
  region: string | null;
  allCities?: string[];
  allProvinces?: string[];
  allShopNames?: string[];
  distance?: string | null;
  total_distance_meters: number | null;
}

// Posts table
export const posts = pgTable("posts", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  caption: text("caption").notNull(),
  translations: jsonb("translations").$type<Record<string, string>>().notNull().default({}),
  image_url: text("image_url"), // Fallback voor oude posts
  images: jsonb("images"), // Array van foto URLs (1-4)
  shop_id: uuid("shop_id").references(() => shops.id, { onDelete: "set null" }),
  route_id: uuid("route_id").references(() => routes.id, { onDelete: "set null" }),
  as_shop_id: uuid("as_shop_id").references(() => shops.id, { onDelete: "set null" }), // Als owner post
  category: text("category").notNull(),
  like_count: integer("like_count").notNull().default(0),
  comment_count: integer("comment_count").notNull().default(0),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
  share_image_url: text("share_image_url"),

  // Moderation fields
  is_hidden: boolean("is_hidden").notNull().default(false),
  report_count: integer("report_count").notNull().default(0),
  hidden_at: timestamp("hidden_at"),
  moderation_status: moderationStatusEnum("moderation_status").notNull().default("green"),
  moderation_metadata: jsonb("moderation_metadata"),
  price_paid: integer("price_paid"), // In cents, optional
  currency: text("currency").notNull().default("EUR"),
}, (table) => ({
  userIdIdx: index("posts_user_id_idx").on(table.user_id),
  shopIdIdx: index("posts_shop_id_idx").on(table.shop_id),
  routeIdIdx: index("posts_route_id_idx").on(table.route_id),
  categoryIdx: index("posts_category_idx").on(table.category),
  createdAtIdx: index("posts_created_at_idx").on(table.created_at),
}));

export type DbPost = InferSelectModel<typeof posts>;
export type InsertDbPost = InferInsertModel<typeof posts>;

// Post likes table
export const postLikes = pgTable("post_likes", {
  id: uuid("id").primaryKey().defaultRandom(),
  post_id: uuid("post_id").notNull().references(() => posts.id, { onDelete: "cascade" }),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  postIdIdx: index("post_likes_post_id_idx").on(table.post_id),
  userIdIdx: index("post_likes_user_id_idx").on(table.user_id),
  postUserUniqueIdx: index("post_likes_post_user_unique_idx").on(table.post_id, table.user_id),
}));

export type PostLike = InferSelectModel<typeof postLikes>;
export type InsertPostLike = InferInsertModel<typeof postLikes>;

// Post comments table
export const postComments = pgTable("post_comments", {
  id: uuid("id").primaryKey().defaultRandom(),
  post_id: uuid("post_id").notNull().references(() => posts.id, { onDelete: "cascade" }),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  translations: jsonb("translations").$type<Record<string, string>>().notNull().default({}),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  postIdIdx: index("post_comments_post_id_idx").on(table.post_id),
  userIdIdx: index("post_comments_user_id_idx").on(table.user_id),
  createdAtIdx: index("post_comments_created_at_idx").on(table.created_at),
}));

export type PostComment = InferSelectModel<typeof postComments>;
export type InsertPostComment = InferInsertModel<typeof postComments>;

// User badges table
export const userBadges = pgTable("user_badges", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  badge_type: text("badge_type").notNull(), // 'vonst_week', 'route_week'
  earned_at: timestamp("earned_at").notNull().defaultNow(),
  week_start_date: timestamp("week_start_date", { mode: "date" }).notNull(),
}, (table) => ({
  userIdIdx: index("user_badges_user_id_idx").on(table.user_id),
  badgeTypeIdx: index("user_badges_badge_type_idx").on(table.badge_type),
  userBadgeWeekUniqueIdx: index("user_badges_user_badge_week_unique_idx").on(table.user_id, table.badge_type, table.week_start_date),
}));

export type UserBadge = InferSelectModel<typeof userBadges>;
export type InsertUserBadge = InferInsertModel<typeof userBadges>;

// Weekly winners table
export const weeklyWinners = pgTable("weekly_winners", {
  id: uuid("id").primaryKey().defaultRandom(),
  week_start_date: timestamp("week_start_date", { mode: "date" }).notNull().unique(),
  post_id: uuid("post_id").references(() => posts.id, { onDelete: "set null" }), // vondst van de week
  route_id: uuid("route_id").references(() => routes.id, { onDelete: "set null" }), // route van de week
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  weekStartDateIdx: index("weekly_winners_week_start_date_idx").on(table.week_start_date),
}));

export type WeeklyWinner = InferSelectModel<typeof weeklyWinners>;
export type InsertWeeklyWinner = InferInsertModel<typeof weeklyWinners>;

// ============================================================================
// Organizations + Shops Data Model Tables
// ============================================================================

// Organizations table
export const organizations = pgTable("organizations", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  created_by_user_id: uuid("created_by_user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
});

export type DbOrganization = InferSelectModel<typeof organizations>;
export type InsertDbOrganization = InferInsertModel<typeof organizations>;

// Organization members table
export const organization_members = pgTable("organization_members", {
  id: uuid("id").primaryKey().defaultRandom(),
  organization_id: uuid("organization_id")
    .notNull()
    .references(() => organizations.id, { onDelete: "cascade" }),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  role: orgRoleEnum("role").notNull().default("admin"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  orgUserUniqueIdx: index("organization_members_org_user_unique_idx").on(
    table.organization_id,
    table.user_id
  ),
}));

export type DbOrganizationMember = InferSelectModel<typeof organization_members>;
export type InsertDbOrganizationMember = InferInsertModel<typeof organization_members>;

// Shop admins table
export const shop_admins = pgTable("shop_admins", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id")
    .notNull()
    .references(() => shops.id, { onDelete: "cascade" }),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  role: orgRoleEnum("role").notNull().default("admin"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  shopUserUniqueIdx: index("shop_admins_shop_user_unique_idx").on(table.shop_id, table.user_id),
}));

export type DbShopAdmin = InferSelectModel<typeof shop_admins>;
export type InsertDbShopAdmin = InferInsertModel<typeof shop_admins>;

// Restyle usage table - Insider exclusive feature
export const usage_restyle = pgTable("usage_restyle", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  month: integer("month").notNull(), // YYYYMM format
  used_count: integer("used_count").notNull().default(0),
}, (table) => ({
  userIdMonthUniqueIdx: unique("usage_restyle_user_id_month_unique_idx").on(table.user_id, table.month),
}));

// SpaceBlend usage table - Insider exclusive feature
export const usage_spaceblend = pgTable("usage_spaceblend", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  month: integer("month").notNull(), // YYYYMM format
  used_count: integer("used_count").notNull().default(0),
}, (table) => ({
  userIdMonthUniqueIdx: unique("usage_spaceblend_user_id_month_unique_idx").on(table.user_id, table.month),
}));

export type DbUsageSpaceBlend = InferSelectModel<typeof usage_spaceblend>;
export type InsertDbUsageSpaceBlend = InferInsertModel<typeof usage_spaceblend>;

// Shop profile details
export type DbUsageRestyle = InferSelectModel<typeof usage_restyle>;
export type InsertDbUsageRestyle = InferInsertModel<typeof usage_restyle>;

// Restyle generation archive table (store before + after images and settings)
export const restyle_generations = pgTable("restyle_generations", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  original_image_url: text("original_image_url").notNull(),
  result_image_url: text("result_image_url").notNull(),
  prompt: text("prompt"),
  output_orientation: text("output_orientation").notNull().default("portrait"),
  settings: jsonb("settings"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userIdIdx: index("restyle_generations_user_id_idx").on(table.user_id),
  createdAtIdx: index("restyle_generations_created_at_idx").on(table.created_at),
}));

export type DbRestyleGeneration = InferSelectModel<typeof restyle_generations>;
export type InsertDbRestyleGeneration = InferInsertModel<typeof restyle_generations>;

// Shop profile details (paid-only)
export const shop_profile_details = pgTable("shop_profile_details", {
  shop_id: uuid("shop_id")
    .primaryKey()
    .references(() => shops.id, { onDelete: "cascade" }),
  story: text("story"),
  website_url: text("website_url"),
  instagram_url: text("instagram_url"),
  facebook_url: text("facebook_url"),
  tiktok_url: text("tiktok_url"),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
});

export type DbShopProfileDetails = InferSelectModel<typeof shop_profile_details>;
export type InsertDbShopProfileDetails = InferInsertModel<typeof shop_profile_details>;

// Shop photos (paid-only)
export const shop_photos = pgTable("shop_photos", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id")
    .notNull()
    .references(() => shops.id, { onDelete: "cascade" }),
  image_url: text("image_url").notNull(),
  is_facade: boolean("is_facade").notNull().default(false),
  sort_order: integer("sort_order").notNull().default(0),
  moderation_status: moderationStatusEnum("moderation_status").notNull().default("green"),
  moderation_metadata: jsonb("moderation_metadata"),
  created_at: timestamp("created_at").notNull().defaultNow(),
});

export type DbShopPhoto = InferSelectModel<typeof shop_photos>;
export type InsertDbShopPhoto = InferInsertModel<typeof shop_photos>;

// Shop opening hours
export const shop_opening_hours = pgTable("shop_opening_hours", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id")
    .notNull()
    .references(() => shops.id, { onDelete: "cascade" }),
  weekday: integer("weekday").notNull(), // 0-6: Monday-Sunday
  open_time: text("open_time"), // format: HH:mm:ss
  close_time: text("close_time"), // format: HH:mm:ss
  is_closed: boolean("is_closed").notNull().default(false),
  updated_at: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => ({
  shopWeekdayIdx: index("shop_opening_hours_shop_weekday_idx").on(table.shop_id, table.weekday),
}));

export type DbShopOpeningHours = InferSelectModel<typeof shop_opening_hours>;
export type InsertDbShopOpeningHours = InferInsertModel<typeof shop_opening_hours>;

// Shop categories declared (paid-only)
export const shop_categories_declared = pgTable("shop_categories_declared", {
  shop_id: uuid("shop_id")
    .notNull()
    .references(() => shops.id, { onDelete: "cascade" }),
  category: text("category").notNull(),
  is_available: boolean("is_available").notNull().default(true),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  shopCategoryPk: index("shop_categories_declared_shop_category_pk").on(table.shop_id, table.category),
}));

export type DbShopCategoriesDeclared = InferSelectModel<typeof shop_categories_declared>;
export type InsertDbShopCategoriesDeclared = InferInsertModel<typeof shop_categories_declared>;

// Shop offers table
export const shop_offers = pgTable("shop_offers", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id")
    .notNull()
    .references(() => shops.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description"),
  price_cents: integer("price_cents").notNull(),
  image_url: text("image_url"),
  status: offerStatusEnum("status").notNull().default("active"),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
  last_published_at: timestamp("last_published_at").notNull().defaultNow(),
}, (table) => ({
  shopIdStatusIdx: index("shop_offers_shop_id_status_idx").on(table.shop_id, table.status),
  statusIdx: index("shop_offers_status_idx").on(table.status),
}));

export type DbShopOffer = InferSelectModel<typeof shop_offers>;
export type InsertDbShopOffer = InferInsertModel<typeof shop_offers>;

// Offer reservations table
export const offer_reservations = pgTable("offer_reservations", {
  id: uuid("id").primaryKey().defaultRandom(),
  offer_id: uuid("offer_id")
    .notNull()
    .references(() => shop_offers.id, { onDelete: "cascade" }),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  status: reservationStatusEnum("status").notNull().default("active"),
  fee_cents: integer("fee_cents").notNull(),
  stripe_checkout_session_id: text("stripe_checkout_session_id"),
  stripe_payment_intent_id: text("stripe_payment_intent_id"),
  expires_at: timestamp("expires_at").notNull(),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  offerIdIdx: index("offer_reservations_offer_id_idx").on(table.offer_id),
  userIdIdx: index("offer_reservations_user_id_idx").on(table.user_id),
  statusIdx: index("offer_reservations_status_idx").on(table.status),
  expiresAtIdx: index("offer_reservations_expires_at_idx").on(table.expires_at),
  stripeSessionIdx: index("offer_reservations_stripe_session_idx").on(table.stripe_checkout_session_id),
}));

export type DbOfferReservation = InferSelectModel<typeof offer_reservations>;
export type InsertDbOfferReservation = InferInsertModel<typeof offer_reservations>;

// Shop reviews table (3-dimensional ratings)
export const shop_reviews = pgTable("shop_reviews", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id")
    .notNull()
    .references(() => shops.id, { onDelete: "cascade" }),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  rating_atmosphere: integer("rating_atmosphere")
    .notNull()
    .$type<1 | 2 | 3 | 4 | 5>(),
  rating_service: integer("rating_service")
    .notNull()
    .$type<1 | 2 | 3 | 4 | 5>(),
  rating_price: integer("rating_price")
    .notNull()
    .$type<1 | 2 | 3 | 4 | 5>(), // Keeping column name for DB compatibility, but logically "Assortment"
  price_level: integer("price_level").$type<1 | 2 | 3 | 4 | 5>(), // 1=Cheap, 3=Avg, 5=Expensive
  parking_rating: integer("parking_rating").$type<1 | 2 | 3>(), // 1=Hard, 2=Ok, 3=Easy
  comment: text("comment"),
  meta_has_charity: boolean("meta_has_charity"),
  meta_has_cash_payment: boolean("meta_has_cash_payment"),
  meta_has_card_payment: boolean("meta_has_card_payment"),
  translations: jsonb("translations").$type<Record<string, string>>().notNull().default({}),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  shopUserUniqueIdx: index("shop_reviews_shop_user_unique_idx").on(table.shop_id, table.user_id),
  shopIdCreatedAtIdx: index("shop_reviews_shop_id_created_at_idx").on(table.shop_id, table.created_at),
  userIdIdx: index("shop_reviews_user_id_idx").on(table.user_id),
}));

export type DbShopReview = InferSelectModel<typeof shop_reviews>;
export type InsertDbShopReview = InferInsertModel<typeof shop_reviews>;

// Review found categories table
export const review_found_categories = pgTable("review_found_categories", {
  review_id: uuid("review_id")
    .notNull()
    .references(() => shop_reviews.id, { onDelete: "cascade" }),
  category: text("category").notNull(),
}, (table) => ({
  reviewCategoryPk: index("review_found_categories_review_category_pk").on(table.review_id, table.category),
}));

export type DbReviewFoundCategory = InferSelectModel<typeof review_found_categories>;
export type InsertDbReviewFoundCategory = InferInsertModel<typeof review_found_categories>;

// Review photos table - stores photos uploaded with reviews (max 4 per review)
export const review_photos = pgTable("review_photos", {
  id: uuid("id").primaryKey().defaultRandom(),
  review_id: uuid("review_id")
    .notNull()
    .references(() => shop_reviews.id, { onDelete: "cascade" }),
  photo_url: text("photo_url").notNull(), // URL to uploaded photo
  display_order: integer("display_order").notNull().default(0), // Order for displaying photos (0-3)
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  reviewIdIdx: index("review_photos_review_id_idx").on(table.review_id),
  reviewIdOrderIdx: index("review_photos_review_id_order_idx").on(table.review_id, table.display_order),
}));

export type DbReviewPhoto = InferSelectModel<typeof review_photos>;
export type InsertDbReviewPhoto = InferInsertModel<typeof review_photos>;

// Route reviews table
export const route_reviews = pgTable("route_reviews", {
  id: uuid("id").primaryKey().defaultRandom(),
  route_id: uuid("route_id")
    .notNull()
    .references(() => routes.id, { onDelete: "cascade" }),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  rating: integer("rating").notNull().$type<1 | 2 | 3 | 4 | 5>(),
  comment: text("comment"),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  routeUserUniqueIdx: index("route_reviews_route_user_unique_idx").on(table.route_id, table.user_id),
  routeIdCreatedAtIdx: index("route_reviews_route_id_created_at_idx").on(table.route_id, table.created_at),
  userIdIdx: index("route_reviews_user_id_idx").on(table.user_id),
}));

export type DbRouteReview = InferSelectModel<typeof route_reviews>;
export type InsertDbRouteReview = InferInsertModel<typeof route_reviews>;

// Route review photos
export const route_review_photos = pgTable("route_review_photos", {
  id: uuid("id").primaryKey().defaultRandom(),
  review_id: uuid("review_id")
    .notNull()
    .references(() => route_reviews.id, { onDelete: "cascade" }),
  photo_url: text("photo_url").notNull(),
  display_order: integer("display_order").notNull().default(0),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  reviewIdIdx: index("route_review_photos_review_id_idx").on(table.review_id),
}));

export type DbRouteReviewPhoto = InferSelectModel<typeof route_review_photos>;
export type InsertDbRouteReviewPhoto = InferInsertModel<typeof route_review_photos>;

// Shop category signals table
export const shop_category_signals = pgTable("shop_category_signals", {
  shop_id: uuid("shop_id")
    .notNull()
    .references(() => shops.id, { onDelete: "cascade" }),
  category: text("category").notNull(),
  unique_user_count: integer("unique_user_count").notNull().default(0),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  shopCategoryPk: index("shop_category_signals_shop_category_pk").on(table.shop_id, table.category),
  shopIdIdx: index("shop_category_signals_shop_id_idx").on(table.shop_id),
}));

export type DbShopCategorySignal = InferSelectModel<typeof shop_category_signals>;
export type InsertDbShopCategorySignal = InferInsertModel<typeof shop_category_signals>;

// ============================================================================
// Batch Job System voor Shop Updates
// ============================================================================

// Batch jobs table
export const shopBatchJobs = pgTable("shop_batch_jobs", {
  id: uuid("id").primaryKey().defaultRandom(),
  status: batchJobStatusEnum("status").notNull().default("pending"),
  job_type: text("job_type").notNull().default("full"), // full, coordinates_only, reviews_only
  total_shops: integer("total_shops").notNull(),
  processed_shops: integer("processed_shops").notNull().default(0),
  successful_shops: integer("successful_shops").notNull().default(0),
  failed_shops: integer("failed_shops").notNull().default(0),
  geocoded_count: integer("geocoded_count").notNull().default(0),
  reviews_updated_count: integer("reviews_updated_count").notNull().default(0),
  current_batch: integer("current_batch").notNull().default(0),
  total_batches: integer("total_batches").notNull(),
  batch_size: integer("batch_size").notNull().default(100),
  started_at: timestamp("started_at"),
  completed_at: timestamp("completed_at"),
  paused_at: timestamp("paused_at"),
  error_message: text("error_message"),
  created_by_user_id: uuid("created_by_user_id").references(() => users.id),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  statusIdx: index("shop_batch_jobs_status_idx").on(table.status),
  createdAtIdx: index("shop_batch_jobs_created_at_idx").on(table.created_at),
  createdByIdx: index("shop_batch_jobs_created_by_idx").on(table.created_by_user_id),
  jobTypeIdx: index("shop_batch_jobs_job_type_idx").on(table.job_type),
}));

export type DbShopBatchJob = InferSelectModel<typeof shopBatchJobs>;
export type InsertDbShopBatchJob = InferInsertModel<typeof shopBatchJobs>;

// Batch job items table
export const shopBatchJobItems = pgTable("shop_batch_job_items", {
  id: uuid("id").primaryKey().defaultRandom(),
  batch_job_id: uuid("batch_job_id").notNull().references(() => shopBatchJobs.id, { onDelete: "cascade" }),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }),
  status: batchJobItemStatusEnum("status").notNull().default("pending"),
  geocoded: boolean("geocoded").notNull().default(false),
  reviews_updated: boolean("reviews_updated").notNull().default(false),
  error_message: text("error_message"),
  processed_at: timestamp("processed_at"),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  batchJobIdIdx: index("shop_batch_job_items_batch_job_id_idx").on(table.batch_job_id),
  shopIdIdx: index("shop_batch_job_items_shop_id_idx").on(table.shop_id),
  statusIdx: index("shop_batch_job_items_status_idx").on(table.status),
  batchStatusIdx: index("shop_batch_job_items_batch_status_idx").on(table.batch_job_id, table.status),
}));

export type DbShopBatchJobItem = InferSelectModel<typeof shopBatchJobItems>;
export type InsertDbShopBatchJobItem = InferInsertModel<typeof shopBatchJobItems>;

// ============================================================================
// User Interactions (Favorites, Wishlists, Usage)
// ============================================================================

// Shop Favorites table
export const shop_favorites = pgTable("shop_favorites", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userShopUniqueIdx: index("shop_favorites_user_shop_unique_idx").on(table.user_id, table.shop_id),
}));

export type DbShopFavorite = InferSelectModel<typeof shop_favorites>;
export type InsertDbShopFavorite = InferInsertModel<typeof shop_favorites>;

// Shop Wishlist table (Te bezoeken)
export const shop_wishlist = pgTable("shop_wishlist", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userShopUniqueIdx: index("shop_wishlist_user_shop_unique_idx").on(table.user_id, table.shop_id),
}));

export type DbShopWishlist = InferSelectModel<typeof shop_wishlist>;
export type InsertDbShopWishlist = InferInsertModel<typeof shop_wishlist>;

// Route Saves table (Favoriete Routes)
export const route_favorites = pgTable("route_favorites", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  route_id: uuid("route_id").notNull().references(() => routes.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userRouteUniqueIdx: index("route_favorites_user_route_unique_idx").on(table.user_id, table.route_id),
}));

export type DbRouteFavorite = InferSelectModel<typeof route_favorites>;
export type InsertDbRouteFavorite = InferInsertModel<typeof route_favorites>;

// Route Wishlist table (Te rijden)
export const route_wishlist = pgTable("route_wishlist", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  route_id: uuid("route_id").notNull().references(() => routes.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userRouteUniqueIdx: index("route_wishlist_user_route_unique_idx").on(table.user_id, table.route_id),
}));

export type DbRouteWishlist = InferSelectModel<typeof route_wishlist>;
export type InsertDbRouteWishlist = InferInsertModel<typeof route_wishlist>;

// User Route Usage (Tracking how many times a user has "used/driven" a route)
export const user_route_usage = pgTable("user_route_usage", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  route_id: uuid("route_id").notNull().references(() => routes.id, { onDelete: "cascade" }),
  usage_count: integer("usage_count").notNull().default(1),
  last_used_at: timestamp("last_used_at").notNull().defaultNow(),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userRouteUniqueIdx: index("user_route_usage_user_route_unique_idx").on(table.user_id, table.route_id),
}));

export type DbUserRouteUsage = InferSelectModel<typeof user_route_usage>;
export type InsertDbUserRouteUsage = InferInsertModel<typeof user_route_usage>;

// Relations for User Interactions
export const shopFavoritesRelations = relations(shop_favorites, ({ one }) => ({
  shop: one(shops, {
    fields: [shop_favorites.shop_id],
    references: [shops.id],
  }),
  user: one(users, {
    fields: [shop_favorites.user_id],
    references: [users.id],
  }),
}));

export const shopWishlistRelations = relations(shop_wishlist, ({ one }) => ({
  shop: one(shops, {
    fields: [shop_wishlist.shop_id],
    references: [shops.id],
  }),
  user: one(users, {
    fields: [shop_wishlist.user_id],
    references: [users.id],
  }),
}));

export const routeFavoritesRelations = relations(route_favorites, ({ one }) => ({
  route: one(routes, {
    fields: [route_favorites.route_id],
    references: [routes.id],
  }),
  user: one(users, {
    fields: [route_favorites.user_id],
    references: [users.id],
  }),
}));

export const userRouteUsageRelations = relations(user_route_usage, ({ one }) => ({
  route: one(routes, {
    fields: [user_route_usage.route_id],
    references: [routes.id],
  }),
  user: one(users, {
    fields: [user_route_usage.user_id],
    references: [users.id],
  }),
}));

export const routeWishlistRelations = relations(route_wishlist, ({ one }) => ({
  route: one(routes, {
    fields: [route_wishlist.route_id],
    references: [routes.id],
  }),
  user: one(users, {
    fields: [route_wishlist.user_id],
    references: [users.id],
  }),
}));

export const item_wishlist = pgTable("item_wishlist", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  item_name: text("item_name").notNull(),
  image_url: text("image_url"),
  image_thumbnail_url: text("image_thumbnail_url"),
  dimensions: text("dimensions"),
  guide_price: real("guide_price"),
  notes: text("notes"),
  is_found: boolean("is_found").default(false),
  found_post_id: uuid("found_post_id").references(() => posts.id, { onDelete: "set null" }),
  is_private: boolean("is_private").notNull().default(false),
  created_at: timestamp("created_at").notNull().defaultNow(),
  found_at: timestamp("found_at"),
}, (table) => ({
  userIdx: index("idx_item_wishlist_user_id").on(table.user_id),
}));

export type ItemWishlist = InferSelectModel<typeof item_wishlist>;
export type InsertItemWishlist = InferInsertModel<typeof item_wishlist>;

export const itemWishlistRelations = relations(item_wishlist, ({ one }) => ({
  user: one(users, {
    fields: [item_wishlist.user_id],
    references: [users.id],
  }),
  foundPost: one(posts, {
    fields: [item_wishlist.found_post_id],
    references: [posts.id],
  }),
}));

// ── Wishlist followers ──────────────────────────────────────────────
export const wishlist_followers = pgTable("wishlist_followers", {
  id: uuid("id").primaryKey().defaultRandom(),
  follower_id: uuid("follower_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  following_id: uuid("following_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  followerIdx: index("idx_wl_followers_follower").on(table.follower_id),
  followingIdx: index("idx_wl_followers_following").on(table.following_id),
  uniqueFollow: uniqueIndex("uq_wl_follower_following").on(table.follower_id, table.following_id),
}));

export type WishlistFollower = InferSelectModel<typeof wishlist_followers>;

export const wishlistFollowerRelations = relations(wishlist_followers, ({ one }) => ({
  follower: one(users, { fields: [wishlist_followers.follower_id], references: [users.id], relationName: "wishlistFollowing" }),
  following: one(users, { fields: [wishlist_followers.following_id], references: [users.id], relationName: "wishlistFollowers" }),
}));

// ── Wishlist invitations ────────────────────────────────────────────
export const wishlist_invitations = pgTable("wishlist_invitations", {
  id: uuid("id").primaryKey().defaultRandom(),
  inviter_id: uuid("inviter_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  invite_token: text("invite_token").notNull().unique(),
  invited_via: text("invited_via").notNull().default("link"),
  invited_contact: text("invited_contact"),
  accepted_by: uuid("accepted_by").references(() => users.id, { onDelete: "set null" }),
  accepted_at: timestamp("accepted_at"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  tokenIdx: index("idx_wl_invitations_token").on(table.invite_token),
  inviterIdx: index("idx_wl_invitations_inviter").on(table.inviter_id),
}));

export type WishlistInvitation = InferSelectModel<typeof wishlist_invitations>;

// Locations table for Dutch cities lookup
export const locations = pgTable("locations", {
  id: uuid("id").primaryKey().defaultRandom(),
  city: text("city").notNull(),
  municipality: text("municipality").notNull(),
  province: text("province").notNull(),
  search_vector: text("search_vector"),
});

export type DbLocation = InferSelectModel<typeof locations>;
export type InsertDbLocation = InferInsertModel<typeof locations>;

// Geocoding cache table for persistent storage of geocoding results
export const geocoding_cache = pgTable("geocoding_cache", {
  id: uuid("id").primaryKey().defaultRandom(),
  query: text("query").notNull(),
  normalized_query: text("normalized_query").notNull().unique(),
  lat: real("lat").notNull(),
  lng: real("lng").notNull(),
  formatted_address: text("formatted_address"),
  place_id: text("place_id"),
  source: text("source").notNull(), // 'google', 'osm', 'static'
  confidence: real("confidence"), // 0.00 to 1.00
  created_at: timestamp("created_at").defaultNow(),
  last_accessed_at: timestamp("last_accessed_at").defaultNow(),
  access_count: integer("access_count").default(1),
  deleted_at: timestamp("deleted_at"),
});

export type GeocodingCacheEntry = InferSelectModel<typeof geocoding_cache>;
export type InsertGeocodingCacheEntry = InferInsertModel<typeof geocoding_cache>;

// ============================================================================
// Route Caching System
// ============================================================================

// Route cache table - stores calculated routes to minimize Google API calls
export const route_cache = pgTable("route_cache", {
  id: uuid("id").primaryKey().defaultRandom(),
  route_hash: text("route_hash").notNull().unique(), // MD5 hash of waypoints for cache lookup
  waypoints: jsonb("waypoints").notNull(), // Array of {lat, lng, shopId?}
  polyline: text("polyline").notNull(), // Encoded polyline from Google
  distance_meters: integer("distance_meters").notNull(),
  duration_seconds: integer("duration_seconds").notNull(),
  bounds: jsonb("bounds").notNull(), // {northeast: {lat, lng}, southwest: {lat, lng}}
  source: text("source").notNull(), // 'google' | 'cached'
  created_at: timestamp("created_at").defaultNow(),
  last_accessed_at: timestamp("last_accessed_at").defaultNow(),
  access_count: integer("access_count").default(1),
  is_popular: boolean("is_popular").default(false), // Auto-set if access_count > 10
  expires_at: timestamp("expires_at"), // NULL = never expires, set for seasonal routes
}, (table) => ({
  hashIdx: index("route_cache_hash_idx").on(table.route_hash),
  popularIdx: index("route_cache_popular_idx").on(table.is_popular),
  accessedIdx: index("route_cache_accessed_idx").on(table.last_accessed_at),
}));

export type RouteCacheEntry = InferSelectModel<typeof route_cache>;
export type InsertRouteCacheEntry = InferInsertModel<typeof route_cache>;

// Route analytics table - tracks API usage and costs
export const route_analytics = pgTable("route_analytics", {
  id: uuid("id").primaryKey().defaultRandom(),
  date: timestamp("date").notNull(), // Date of analytics (daily granularity)
  total_requests: integer("total_requests").default(0),
  cache_hits: integer("cache_hits").default(0),
  cache_misses: integer("cache_misses").default(0),
  google_api_calls: integer("google_api_calls").default(0),
  estimated_cost_usd: real("estimated_cost_usd").default(0), // $0.005 per Directions API call
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
}, (table) => ({
  dateIdx: index("route_analytics_date_idx").on(table.date),
}));

export type RouteAnalyticsEntry = InferSelectModel<typeof route_analytics>;
export type InsertRouteAnalyticsEntry = InferInsertModel<typeof route_analytics>;

// ============================================================================
// Region Boundaries (Provinces & Cities)
// ============================================================================

// Region boundaries table - for province/city search with map zoom
export const region_boundaries = pgTable("region_boundaries", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(), // "Noord-Holland", "Amsterdam"
  type: text("type").notNull(), // 'province' | 'city' | 'municipality'
  province: text("province"), // Parent province for cities
  center_lat: real("center_lat").notNull(),
  center_lng: real("center_lng").notNull(),
  zoom_level: integer("zoom_level").notNull(), // 9 for province, 12 for city
  bounds: jsonb("bounds").notNull(), // {northeast: {lat, lng}, southwest: {lat, lng}}
  polygon: jsonb("polygon"), // Detailed polygon for exact boundaries (optional)
  population: integer("population"), // For ranking/display
  aliases: jsonb("aliases"), // ["NH", "N-H"] for Noord-Holland
  country: text("country").notNull().default("NL"), // "NL", "BE", "FR"
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
}, (table) => ({
  nameIdx: index("region_name_idx").on(table.name),
  typeIdx: index("region_type_idx").on(table.type),
  countryIdx: index("region_country_idx").on(table.country),
  provinceIdx: index("region_province_idx").on(table.province),
  nameTypeCountryUniqueIdx: uniqueIndex("region_name_type_country_unique_idx").on(table.name, table.type, table.country),
}));

export type RegionBoundary = InferSelectModel<typeof region_boundaries>;
export type InsertRegionBoundary = InferInsertModel<typeof region_boundaries>;

// ============================================================================
// Content Moderation & Reporting
// ============================================================================

// Report status enum
export const reportStatusEnum = pgEnum("report_status", [
  "pending",
  "reviewed",
  "dismissed",
  "actioned"
]);

// Content reports table
export const content_reports = pgTable("content_reports", {
  id: uuid("id").primaryKey().defaultRandom(),
  reporter_user_id: uuid("reporter_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),

  // Content being reported (one of these will be set)
  post_id: uuid("post_id").references(() => posts.id, { onDelete: "cascade" }),
  photo_id: uuid("photo_id").references(() => shop_photos.id, { onDelete: "cascade" }),
  review_id: uuid("review_id").references(() => shop_reviews.id, { onDelete: "cascade" }),

  reason: reportReasonEnum("reason").notNull(),
  description: text("description"), // Optional additional context
  status: reportStatusEnum("status").notNull().default("pending"),

  // Admin review
  reviewed_by_user_id: uuid("reviewed_by_user_id").references(() => users.id),
  reviewed_at: timestamp("reviewed_at"),
  admin_notes: text("admin_notes"),

  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  reporterIdx: index("content_reports_reporter_idx").on(table.reporter_user_id),
  postIdx: index("content_reports_post_idx").on(table.post_id),
  photoIdx: index("content_reports_photo_idx").on(table.photo_id),
  reviewIdx: index("content_reports_review_idx").on(table.review_id),
  statusIdx: index("content_reports_status_idx").on(table.status),
}));

export type DbContentReport = InferSelectModel<typeof content_reports>;
export type InsertDbContentReport = InferInsertModel<typeof content_reports>;

// Add moderation fields to shop_photos (used for posts)
// Note: We'll add these via migration:
// - report_count: integer (number of reports)
// - is_hidden: boolean (auto_hidden at 2+ reports)
// - hidden_at: timestamp
// - moderation_status: enum('visible', 'auto_hidden', 'admin_hidden', 'removed')

// Activity logs table for admin audit trail
export const activityLogs = pgTable("activity_logs", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "set null" }), // Can be null if user deleted
  action: text("action").notNull(), // e.g., "update_user_tier", "verify_shop", "delete_post"
  resource_type: text("resource_type").notNull(), // e.g., "user", "shop", "post"
  resource_id: uuid("resource_id"), // ID of the affected resource
  details: jsonb("details"), // Additional context about the action
  ip_address: text("ip_address"),
  timestamp: timestamp("timestamp").notNull().defaultNow(),
}, (table) => ({
  userIdx: index("activity_logs_user_idx").on(table.user_id),
  resourceIdx: index("activity_logs_resource_idx").on(table.resource_type, table.resource_id),
  timestampIdx: index("activity_logs_timestamp_idx").on(table.timestamp),
}));

export type ActivityLog = InferSelectModel<typeof activityLogs>;
export type InsertActivityLog = InferInsertModel<typeof activityLogs>;

// API Usage Stats table for tracking API calls and performance
export const apiUsageStats = pgTable("api_usage_stats", {
  id: uuid("id").primaryKey().defaultRandom(),
  api_name: text("api_name").notNull(), // 'google_places', 'cache', 'rate_limit'
  request_count: integer("request_count").default(0),
  success_count: integer("success_count").default(0),
  error_count: integer("error_count").default(0),
  estimated_cost_cents: integer("estimated_cost_cents").default(0), // in cents
  avg_response_time_ms: integer("avg_response_time_ms").default(0),
  period_start: timestamp("period_start").notNull(),
  period_end: timestamp("period_end"),
  details: jsonb("details"),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  apiNameIdx: index("api_usage_stats_api_name_idx").on(table.api_name),
  periodIdx: index("api_usage_stats_period_idx").on(table.period_start),
}));

export type ApiUsageStat = InferSelectModel<typeof apiUsageStats>;
export type InsertApiUsageStat = InferInsertModel<typeof apiUsageStats>;

// Lightweight app counters (global/per-country totals)
export const app_metrics = pgTable("app_metrics", {
  key: text("key").primaryKey(),
  value: integer("value").notNull().default(0),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  updatedAtIdx: index("app_metrics_updated_at_idx").on(table.updated_at),
}));

export type AppMetric = InferSelectModel<typeof app_metrics>;
export type InsertAppMetric = InferInsertModel<typeof app_metrics>;

// Shop Analytics table for tracking engagement
export const shop_analytics = pgTable("shop_analytics", {
  id: uuid("id").primaryKey().defaultRandom(),
  shop_id: uuid("shop_id").notNull().references(() => shops.id, { onDelete: "cascade" }),
  event_type: text("event_type").notNull(), // 'view_profile', 'nav_profile', 'nav_route'
  route_id: uuid("route_id"), // Optional: ID of the route if type is 'nav_route'
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  shopIdIdx: index("shop_analytics_shop_id_idx").on(table.shop_id),
  typeIdx: index("shop_analytics_type_idx").on(table.event_type),
  dateIdx: index("shop_analytics_date_idx").on(table.created_at),
}));

export type InsertDbShopAnalytics = InferInsertModel<typeof shop_analytics>;





// Insider Scanner - History of AI analyses
export const scansArchive = pgTable("scans_archive", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id),
  type: text("type").$type<"flash" | "pro">(),
  ai_title: text("ai_title"),
  first_image_url: text("first_image_url"),
  full_analysis: jsonb("full_analysis"),
  market_links: jsonb("market_links"), // Array of {platform, title, link, img_url}
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userIdIdx: index("scans_archive_user_id_idx").on(table.user_id),
  createdAtIdx: index("scans_archive_created_at_idx").on(table.created_at),
}));

export const scansArchiveRelations = relations(scansArchive, ({ one }) => ({
  user: one(users, {
    fields: [scansArchive.user_id],
    references: [users.id],
  }),
}));

export type DbScanArchive = InferSelectModel<typeof scansArchive>;
export type InsertDbScanArchive = InferInsertModel<typeof scansArchive>;


// ============================================================================
// Restyle Projects Model
// ============================================================================

// Restyle Projects table
export const restyleProjects = pgTable("restyle_projects", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  shop_id: uuid("shop_id").references(() => shops.id, { onDelete: "set null" }), // "Gevonden bij"
  original_image_url: text("original_image_url"),
  ai_result_url: text("ai_result_url").notNull(),
  share_image_url: text("share_image_url").notNull(), // The before/after composition
  real_result_image_url: text("real_result_image_url"), // User's own photo of the manual result
  description: text("description"),
  prompt_data: jsonb("prompt_data"), // Store the settings used
  is_public: boolean("is_public").notNull().default(false),
  price_paid: integer("price_paid"), // In cents, optional
  likes_count: integer("likes_count").notNull().default(0),
  view_count: integer("view_count").notNull().default(0),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userIdIdx: index("restyle_projects_user_id_idx").on(table.user_id),
  shopIdIdx: index("restyle_projects_shop_id_idx").on(table.shop_id),
  isPublicIdx: index("restyle_projects_is_public_idx").on(table.is_public),
  createdAtIdx: index("restyle_projects_created_at_idx").on(table.created_at),
}));

export type DbRestyleProject = InferSelectModel<typeof restyleProjects>;
export type InsertDbRestyleProject = InferInsertModel<typeof restyleProjects>;

// Restyle Likes table
export const restyleLikes = pgTable("restyle_likes", {
  id: uuid("id").primaryKey().defaultRandom(),
  restyle_id: uuid("restyle_id").notNull().references(() => restyleProjects.id, { onDelete: "cascade" }),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  restyleIdIdx: index("restyle_likes_restyle_id_idx").on(table.restyle_id),
  userIdIdx: index("restyle_likes_user_id_idx").on(table.user_id),
  restyleUserUniqueIdx: index("restyle_likes_restyle_user_unique_idx").on(table.restyle_id, table.user_id),
}));

// Restyle Comments table
export const restyleComments = pgTable("restyle_comments", {
  id: uuid("id").primaryKey().defaultRandom(),
  restyle_id: uuid("restyle_id").notNull().references(() => restyleProjects.id, { onDelete: "cascade" }),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  restyleIdIdx: index("restyle_comments_restyle_id_idx").on(table.restyle_id),
  userIdIdx: index("restyle_comments_user_id_idx").on(table.user_id),
  createdAtIdx: index("restyle_comments_created_at_idx").on(table.created_at),
}));

export type RestyleLike = InferSelectModel<typeof restyleLikes>;
export type InsertRestyleLike = InferInsertModel<typeof restyleLikes>;

export const restyleProjectsRelations = relations(restyleProjects, ({ one, many }) => ({
  user: one(users, {
    fields: [restyleProjects.user_id],
    references: [users.id],
  }),
  shop: one(shops, {
    fields: [restyleProjects.shop_id],
    references: [shops.id],
  }),
  likes: many(restyleLikes),
  comments: many(restyleComments),
}));

export const restyleCommentsRelations = relations(restyleComments, ({ one }) => ({
  project: one(restyleProjects, {
    fields: [restyleComments.restyle_id],
    references: [restyleProjects.id],
  }),
  user: one(users, {
    fields: [restyleComments.user_id],
    references: [users.id],
  }),
}));

export const restyleLikesRelations = relations(restyleLikes, ({ one }) => ({
  project: one(restyleProjects, {
    fields: [restyleLikes.restyle_id],
    references: [restyleProjects.id],
  }),
  user: one(users, {
    fields: [restyleLikes.user_id],
    references: [users.id],
  }),
}));

// ============================================================================
// SpaceBlend Model
// ============================================================================

// User Interiors table (max 20 per user)
export const userInteriors = pgTable("user_interiors", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  image_url: text("image_url").notNull(),
  name: text("name"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userIdIdx: index("user_interiors_user_id_idx").on(table.user_id),
}));

export type DbUserInterior = InferSelectModel<typeof userInteriors>;
export type InsertDbUserInterior = InferInsertModel<typeof userInteriors>;

// SpaceBlend Projects table
export const spaceBlendProjects = pgTable("space_blend_projects", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  interior_id: uuid("interior_id").references(() => userInteriors.id, { onDelete: "set null" }),
  furniture_source_url: text("furniture_source_url").notNull(),
  composite_image_url: text("composite_image_url").notNull(),
  result_image_url: text("result_image_url").notNull(),
  share_image_url: text("share_image_url"),
  description: text("description"),
  prompt: text("prompt"),
  is_public: boolean("is_public").notNull().default(false),
  likes_count: integer("likes_count").notNull().default(0),
  view_count: integer("view_count").notNull().default(0),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userIdIdx: index("space_blend_projects_user_id_idx").on(table.user_id),
  interiorIdIdx: index("space_blend_projects_interior_id_idx").on(table.interior_id),
  isPublicIdx: index("space_blend_projects_is_public_idx").on(table.is_public),
}));

export type DbSpaceBlendProject = InferSelectModel<typeof spaceBlendProjects>;
export type InsertDbSpaceBlendProject = InferInsertModel<typeof spaceBlendProjects>;

// SpaceBlend Likes table
export const spaceBlendLikes = pgTable("space_blend_likes", {
  id: uuid("id").primaryKey().defaultRandom(),
  project_id: uuid("project_id").notNull().references(() => spaceBlendProjects.id, { onDelete: "cascade" }),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  projectIdIdx: index("space_blend_likes_project_id_idx").on(table.project_id),
  userIdIdx: index("space_blend_likes_user_id_idx").on(table.user_id),
  projectUserUniqueIdx: uniqueIndex("space_blend_likes_project_user_unique_idx").on(table.project_id, table.user_id),
}));

export type SpaceBlendLike = InferSelectModel<typeof spaceBlendLikes>;
export type InsertSpaceBlendLike = InferInsertModel<typeof spaceBlendLikes>;

// Relations
export const userInteriorsRelations = relations(userInteriors, ({ one, many }) => ({
  user: one(users, {
    fields: [userInteriors.user_id],
    references: [users.id],
  }),
  projects: many(spaceBlendProjects),
}));

export const spaceBlendProjectsRelations = relations(spaceBlendProjects, ({ one, many }) => ({
  user: one(users, {
    fields: [spaceBlendProjects.user_id],
    references: [users.id],
  }),
  interior: one(userInteriors, {
    fields: [spaceBlendProjects.interior_id],
    references: [userInteriors.id],
  }),
  likes: many(spaceBlendLikes),
}));

export const spaceBlendLikesRelations = relations(spaceBlendLikes, ({ one }) => ({
  project: one(spaceBlendProjects, {
    fields: [spaceBlendLikes.project_id],
    references: [spaceBlendProjects.id],
  }),
  user: one(users, {
    fields: [spaceBlendLikes.user_id],
    references: [users.id],
  }),
}));

// ─── Analytics Tracking Tables ────────────────────────────────────────────────

/**
 * analytics_sessions
 * One row per user session. Used for DAU/MAU, session duration, and retention metrics.
 * Written by the frontend (POST /api/analytics/session-start / session-end).
 */
export const analytics_sessions = pgTable("analytics_sessions", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "set null" }),
  session_token: text("session_token").notNull(),          // random client-generated ID
  started_at: timestamp("started_at").notNull().defaultNow(),
  ended_at: timestamp("ended_at"),
  duration_seconds: integer("duration_seconds"),
  country: varchar("country", { length: 8 }),
  device_type: varchar("device_type", { length: 32 }),  // "mobile" | "desktop" | "tablet"
  page_count: integer("page_count").default(1),
  is_returning: boolean("is_returning").default(false),   // had a previous session this month?
}, (t) => ({
  userIdx: index("analytics_sessions_user_idx").on(t.user_id),
  startedIdx: index("analytics_sessions_started_idx").on(t.started_at),
  tokenIdx: index("analytics_sessions_token_idx").on(t.session_token),
}));

export type AnalyticsSession = InferSelectModel<typeof analytics_sessions>;
export type InsertAnalyticsSession = InferInsertModel<typeof analytics_sessions>;

/**
 * route_events
 * Tracks what users do with routes: view, share, or "open in Google Maps" (= start/completion intent).
 */
export const route_events = pgTable("route_events", {
  id: uuid("id").primaryKey().defaultRandom(),
  route_id: uuid("route_id").references(() => routes.id, { onDelete: "cascade" }),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "set null" }),
  event_type: text("event_type").notNull(),   // "view" | "maps_click" | "share"
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  routeIdx: index("route_events_route_idx").on(t.route_id),
  userIdx: index("route_events_user_idx").on(t.user_id),
  createdIdx: index("route_events_created_idx").on(t.created_at),
}));

export type RouteEvent = InferSelectModel<typeof route_events>;
export type InsertRouteEvent = InferInsertModel<typeof route_events>;

/**
 * moderation_log
 * Audit trail for every Gemini moderation check. Enables dashboard stats on:
 *  - % content blocked / flagged / passed
 *  - Blocked content categories (violence vs. explicit vs. hate)
 *  - Volume trends over time
 */
export const moderation_log = pgTable("moderation_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  upload_context: text("upload_context"),    // e.g. "post", "shop-photo", "avatar"
  verdict: text("verdict").notNull(), // "green" | "amber" | "red"
  reason: text("reason"),
  explicit_score: integer("explicit_score"),
  violence_score: integer("violence_score"),
  hate_score: integer("hate_score"),
  is_art: boolean("is_art").default(false),
  model_used: text("model_used").default("gemini"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  verdictIdx: index("moderation_log_verdict_idx").on(t.verdict),
  createdIdx: index("moderation_log_created_idx").on(t.created_at),
}));

export type ModerationLog = InferSelectModel<typeof moderation_log>;
export type InsertModerationLog = InferInsertModel<typeof moderation_log>;

// ─── AVG / Privacy Compliance Tables ────────────────────────────────────────

/**
 * consent_log
 * Audit trail for user consent changes. Required for legal compliance (GDPR/AVG).
 */
export const consent_log = pgTable("consent_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  consent_type: consentTypeEnum("consent_type").notNull(),
  granted: boolean("granted").notNull(),
  version: text("version").notNull(), // Version of the document/policy agreed to
  ip_hash: text("ip_hash"),           // Anonymized IP for audit trail
  user_agent: text("user_agent"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  userIdx: index("consent_log_user_idx").on(t.user_id),
}));

export type ConsentLog = InferSelectModel<typeof consent_log>;
export type InsertConsentLog = InferInsertModel<typeof consent_log>;

/**
 * deletion_requests
 * Tracks requests for account deletion (Right to be Forgotten).
 * Accounts are typically purged after a 30-day "grace period".
 */
export const deletion_requests = pgTable("deletion_requests", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  requested_at: timestamp("requested_at").notNull().defaultNow(),
  scheduled_for: timestamp("scheduled_for").notNull(), // usually requested_at + 30 days
  status: deletionStatusEnum("status").notNull().default("pending"),
  completed_at: timestamp("completed_at"),
}, (t) => ({
  userIdx: index("deletion_requests_user_idx").on(t.user_id),
  statusIdx: index("deletion_requests_status_idx").on(t.status),
}));

export type DeletionRequest = InferSelectModel<typeof deletion_requests>;
export type InsertDeletionRequest = InferInsertModel<typeof deletion_requests>;

/**
 * admin_permissions
 * Granular permissions for admin users to control access to sensitive data and actions.
 */
export const admin_permissions = pgTable("admin_permissions", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  can_manage_users: boolean("can_manage_users").notNull().default(false),
  can_manage_shops: boolean("can_manage_shops").notNull().default(true),
  can_view_analytics: boolean("can_view_analytics").notNull().default(true),
  can_manage_billing: boolean("can_manage_billing").notNull().default(false),
  can_access_pii: boolean("can_access_pii").notNull().default(false), // Access to email, addresses, etc.
  can_export_data: boolean("can_export_data").notNull().default(false),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  userIdx: index("admin_permissions_user_idx").on(t.user_id),
}));

export type AdminPermission = InferSelectModel<typeof admin_permissions>;
export type InsertAdminPermission = InferInsertModel<typeof admin_permissions>;

/**
 * access_logs
 * Tracks access to sensitive data (PII) for auditing.
 */
export const access_logs = pgTable("access_logs", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Who accessed it
  target_user_id: uuid("target_user_id").references(() => users.id, { onDelete: "set null" }), // Whose data was it
  action: text("action").notNull(), // e.g. "VIEW_PII", "EXPORT_DATA", "DELETE_ACCOUNT"
  resource: text("resource").notNull(), // e.g. "users", "profiles"
  ip_hash: text("ip_hash"),
  user_agent: text("user_agent"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  userIdx: index("access_logs_user_idx").on(t.user_id),
}));

export type AccessLog = InferSelectModel<typeof access_logs>;
export type InsertAccessLog = InferInsertModel<typeof access_logs>;

// Routing Logs for analytics and monitoring
export const routing_logs = pgTable("routing_logs", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "set null" }),
  provider: routingProviderEnum("provider").notNull(),
  country: varchar("country", { length: 10 }).notNull().default("NL"),
  success: boolean("success").notNull().default(true),
  latency_ms: integer("latency_ms"),
  error_message: text("error_message"),
  request_payload: jsonb("request_payload"),
  response_summary: jsonb("response_summary"),
  created_at: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  providerIdx: index("routing_logs_provider_idx").on(table.provider),
  countryIdx: index("routing_logs_country_idx").on(table.country),
  createdAtIdx: index("routing_logs_created_at_idx").on(table.created_at),
}));

export type RoutingLog = InferSelectModel<typeof routing_logs>;
export type InsertRoutingLog = InferInsertModel<typeof routing_logs>;

// User Sessions for concurrency tracking
export const user_sessions = pgTable("user_sessions", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  last_active_at: timestamp("last_active_at").notNull().defaultNow(),
  is_insider: boolean("is_insider").notNull().default(false),
  country: varchar("country", { length: 10 }).notNull().default("NL"),
}, (table) => ({
  userIdx: index("user_sessions_user_id_idx").on(table.user_id),
  lastActiveIdx: index("user_sessions_last_active_idx").on(table.last_active_at),
}));

export type UserSession = InferSelectModel<typeof user_sessions>;

// Routing Config for per-land fallback ordering & dynamic map URLs
export const routing_config = pgTable("routing_config", {
  id: uuid("id").primaryKey().defaultRandom(),
  land: varchar("land", { length: 10 }).notNull().unique(), // 'nl', 'be', 'fr', 'default'
  tile_url: text("tile_url").notNull(),    // MapTiler / eigen server / OSM
  routing_url: text("routing_url").notNull(), // ORS / GraphHopper / eigen server
  provider_order: text("provider_order").array().notNull().default(sql`ARRAY['ors', 'graphhopper', 'locationiq']::text[]`),
  active: boolean("active").notNull().default(true),
  created_at: timestamp("created_at").notNull().defaultNow(),
  updated_at: timestamp("updated_at").notNull().defaultNow(),
});

export type RoutingConfig = InferSelectModel<typeof routing_config>;
export type InsertRoutingConfig = InferInsertModel<typeof routing_config>;

export const seoCrawlerStats = pgTable("crawler_stats", {
  id: uuid("id").primaryKey().defaultRandom(),
  bot_name: varchar("bot_name", { length: 50 }).notNull(),
  url_crawled: text("url_crawled").notNull(),
  response_code: integer("response_code").notNull(),
  crawl_time: timestamp("crawl_time").notNull().defaultNow(),
}, (table) => ({
  botNameIdx: index("crawler_stats_bot_name_idx").on(table.bot_name),
  crawlTimeIdx: index("crawler_stats_crawl_time_idx").on(table.crawl_time),
}));

export type seoCrawlerStat = InferSelectModel<typeof seoCrawlerStats>;
export type InsertSeoCrawlerStat = InferInsertModel<typeof seoCrawlerStats>;
